import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, type AccommodationDto, type StayRuleDto } from '../lib/api';
import { shortDate, toDay, toIso } from '../lib/format';
import { useT, type MessageKey, type TFunction } from '../lib/i18n';

/**
 * What a stay has to look like before the website will sell it.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS SCREEN HAD TO EXIST
 * ---------------------------------------------------------------------------
 *
 * Every other part of this was already built: the engine evaluates the rules, the
 * API stores them, the guest site renders "ask us" and "indicative" properly. The
 * operator simply had no way to enter one. So a park could not say the single most
 * important thing it says about a long stay — *at this length, come and talk to
 * us* — and every three-month search came back as an ordinary bookable price.
 *
 * That is the seasonal-contract threshold. A guest asking for ninety nights on a
 * pitch is not looking for a holiday; they are looking for a jaarplaats, and the
 * price for one is not on the tariff card. Quoting the nightly ladder for it is
 * worse than quoting nothing: it is a number the park will not honour.
 *
 * ---------------------------------------------------------------------------
 * THE THRESHOLD IS THE FIRST FORM, NOT A CORNER OF A GENERAL ONE
 * ---------------------------------------------------------------------------
 *
 * `stay_rule` is general — weekdays, windows, channels, minimums, three effects.
 * The threshold is one narrow shape of it: no minimum, a maximum, effect
 * `contact`, covering the whole season. An operator who has to assemble that from
 * six controls will get it wrong once and then not trust it.
 *
 * So the common case is its own form with two fields, and the general editor is
 * underneath for the park that needs Saturday-to-Saturday in the school holidays.
 * Both write the same record; the threshold form only prefills it.
 */

type Run = <T>(fn: () => Promise<T>, ok?: string | ((out: T) => string)) => Promise<T | null>;

export interface RulesProps {
  accommodations: AccommodationDto[];
  /** The season the header is showing. The window defaults to it. */
  year: number;
  run: Run;
  busy?: boolean;
}

const EFFECTS = ['block', 'contact', 'indicative'] as const;
type Effect = (typeof EFFECTS)[number];

const EFFECT_LABEL: Record<Effect, MessageKey> = {
  block: 'rules.effectBlock',
  contact: 'rules.effectContact',
  indicative: 'rules.effectIndicative',
};

const EFFECT_NOTE: Record<Effect, MessageKey> = {
  block: 'rules.effectBlockNote',
  contact: 'rules.effectContactNote',
  indicative: 'rules.effectIndicativeNote',
};

/** Monday first, because that is how a Dutch week is drawn and how the API counts. */
const WEEKDAY_KEYS: MessageKey[] = [
  'rules.sun', 'rules.mon', 'rules.tue', 'rules.wed', 'rules.thu', 'rules.fri', 'rules.sat',
];
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

interface Draft {
  id: string;
  accommodationId: string;
  from: string;
  until: string;
  effect: Effect;
  minNights: string;
  maxNights: string;
  arriveOn: number[];
  departOn: number[];
  label: string;
  note: string;
}

const blank = (year: number): Draft => ({
  id: '',
  accommodationId: '',
  from: `${year}-01-01`,
  until: `${year + 1}-01-01`,
  effect: 'contact',
  minNights: '',
  maxNights: '',
  arriveOn: [],
  departOn: [],
  label: '',
  note: '',
});

/**
 * A rule read back as a sentence.
 *
 * The list would otherwise be a row of numbers and an English word, and the whole
 * risk with this screen is an operator who is not certain what they just wrote.
 */
function describe(t: TFunction, r: StayRuleDto, accommodationLabel: string): string {
  const parts: string[] = [];
  if (r.minNights != null && r.maxNights != null) {
    parts.push(t('rules.saysBetween', { min: r.minNights, max: r.maxNights }));
  } else if (r.minNights != null) {
    parts.push(t('rules.saysUnder', { min: r.minNights }));
  } else if (r.maxNights != null) {
    parts.push(t('rules.saysOver', { max: r.maxNights }));
  }
  const days = (list: number[] | null | undefined, key: MessageKey) => {
    if (!list || list.length === 0 || list.length === 7) return;
    parts.push(t(key, {
      days: WEEK_ORDER.filter((d) => list.includes(d)).map((d) => t(WEEKDAY_KEYS[d])).join(', '),
    }));
  };
  days(r.arriveOn, 'rules.saysArriveOn');
  days(r.departOn, 'rules.saysDepartOn');
  // A rule with no condition at all applies to every stay in its window, which is
  // a legitimate thing to write ("nothing on this type is bookable online") and a
  // dangerous thing to write by accident.
  if (parts.length === 0) parts.push(t('rules.saysAnyStay'));
  return t('rules.sentence', {
    what: parts.join(t('rules.join')),
    accommodation: accommodationLabel,
    effect: t(EFFECT_LABEL[(r.effect ?? 'block') as Effect]),
  });
}

export function Rules({ accommodations, year, run, busy }: RulesProps) {
  const t = useT();
  const [rules, setRules] = useState<StayRuleDto[]>([]);
  const [draft, setDraft] = useState<Draft>(() => blank(year));
  const [general, setGeneral] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  // The two-field form, kept apart from the general draft so switching between
  // them cannot half-fill either.
  const [overNights, setOverNights] = useState('');
  const [overType, setOverType] = useState('');

  const reload = useCallback(async () => {
    const list = await api.stayRules();
    setRules(list);
    return list;
  }, []);

  useEffect(() => { void run(reload); }, [run, reload]);

  // Only the window follows the header's season. Everything else is the
  // operator's, and resetting a half-written rule because they looked at another
  // year would be its own bug.
  useEffect(() => {
    setDraft((d) => (d.id ? d : { ...d, from: `${year}-01-01`, until: `${year + 1}-01-01` }));
  }, [year]);

  const labelOf = useCallback((id: string | null | undefined): string => {
    if (!id) return t('rules.everyType');
    return accommodations.find((a) => a.id === id)?.label ?? id;
  }, [accommodations, t]);

  const sorted = useMemo(
    () => [...rules].sort((a, b) => a.from.localeCompare(b.from) || a.id.localeCompare(b.id)),
    [rules],
  );

  const save = async (body: Omit<StayRuleDto, 'id'> & { id: string }) => {
    setProblem(null);
    const { id, ...rest } = body;
    const out = await run(
      () => api.putStayRule(id, rest).then(reload),
      t('rules.saved'),
    );
    if (out) {
      setDraft(blank(year));
      setOverNights('');
    }
  };

  /** The threshold. Two fields, and the rest is implied. */
  const addThreshold = async () => {
    const n = Number(overNights);
    if (!Number.isInteger(n) || n < 1 || n > 365) {
      setProblem(t('rules.badThreshold'));
      return;
    }
    const scope = overType || 'all';
    await save({
      // Derived from what it says, so entering the same threshold twice replaces
      // it rather than stacking two rules that mean one thing.
      id: `over-${n}n-${scope}-${year}`,
      accommodationId: overType || null,
      from: `${year}-01-01`,
      until: `${year + 1}-01-01`,
      effect: 'contact',
      minNights: null,
      maxNights: n,
      arriveOn: null,
      departOn: null,
      // The guest reads this. Left as the park's own sentence rather than a
      // generic one, because "ask us" and "ask us about a seasonal pitch" are
      // different offers.
      label: t('rules.thresholdLabel'),
      note: t('rules.thresholdNote', { nights: n }),
    });
  };

  const addGeneral = async () => {
    setProblem(null);
    const from = toDay(draft.from);
    const until = toDay(draft.until);
    if (from === null || until === null) { setProblem(t('rules.badDates')); return; }
    if (until <= from) { setProblem(t('rules.badOrder')); return; }
    const min = draft.minNights === '' ? null : Number(draft.minNights);
    const max = draft.maxNights === '' ? null : Number(draft.maxNights);
    for (const v of [min, max]) {
      if (v !== null && (!Number.isInteger(v) || v < 1 || v > 365)) {
        setProblem(t('rules.badNights'));
        return;
      }
    }
    if (min !== null && max !== null && max < min) { setProblem(t('rules.badRange')); return; }
    await save({
      id: draft.id || `rule-${draft.from}-${draft.effect}-${Date.now().toString(36)}`,
      accommodationId: draft.accommodationId || null,
      from: draft.from,
      until: draft.until,
      effect: draft.effect,
      minNights: min,
      maxNights: max,
      arriveOn: draft.arriveOn.length ? draft.arriveOn : null,
      departOn: draft.departOn.length ? draft.departOn : null,
      label: draft.label || null,
      note: draft.note || null,
    });
  };

  const remove = (id: string) =>
    void run(() => api.deleteStayRule(id).then(reload), t('rules.removed'));

  const edit = (r: StayRuleDto) => {
    setGeneral(true);
    setDraft({
      id: r.id,
      accommodationId: r.accommodationId ?? '',
      from: r.from,
      until: r.until,
      effect: (r.effect ?? 'block') as Effect,
      minNights: r.minNights == null ? '' : String(r.minNights),
      maxNights: r.maxNights == null ? '' : String(r.maxNights),
      arriveOn: r.arriveOn ?? [],
      departOn: r.departOn ?? [],
      label: r.label ?? '',
      note: r.note ?? '',
    });
  };

  const toggleDay = (field: 'arriveOn' | 'departOn', day: number) =>
    setDraft((d) => ({
      ...d,
      [field]: d[field].includes(day) ? d[field].filter((x) => x !== day) : [...d[field], day],
    }));

  return (
    <section className="panel">
      <h2>{t('rules.heading')}</h2>
      <p className="note">{t('rules.intro')}</p>

      {problem && <div className="banner bad" role="alert">{problem}</div>}

      {/* ------------------------------------------------------------------ */}
      {/* The threshold                                                      */}
      {/* ------------------------------------------------------------------ */}
      <fieldset className="card">
        <legend>{t('rules.thresholdHeading')}</legend>
        <p className="note">{t('rules.thresholdIntro')}</p>
        <div className="row">
          <label>
            {t('rules.thresholdNights')}
            <input
              type="number" min={1} max={365} value={overNights}
              onChange={(e) => setOverNights(e.target.value)}
            />
          </label>
          <label>
            {t('rules.appliesTo')}
            <select value={overType} onChange={(e) => setOverType(e.target.value)}>
              <option value="">{t('rules.everyType')}</option>
              {accommodations.map((a) => (
                <option key={a.id} value={a.id}>{a.label}</option>
              ))}
            </select>
          </label>
          <button type="button" className="btn primary" disabled={busy} onClick={addThreshold}>
            {t('rules.addThreshold')}
          </button>
        </div>
        {overNights !== '' && Number(overNights) > 0 && (
          <p className="note" role="status">
            {t('rules.thresholdPreview', {
              nights: Number(overNights),
              accommodation: labelOf(overType || null),
              year,
            })}
          </p>
        )}
      </fieldset>

      {/* ------------------------------------------------------------------ */}
      {/* Everything else                                                    */}
      {/* ------------------------------------------------------------------ */}
      <button
        type="button" className="btn" aria-expanded={general}
        onClick={() => setGeneral((g) => !g)}
      >
        {general ? t('rules.hideGeneral') : t('rules.showGeneral')}
      </button>

      {general && (
        <fieldset className="card">
          <legend>{draft.id ? t('rules.editing', { id: draft.id }) : t('rules.generalHeading')}</legend>
          <div className="row">
            <label>
              {t('rules.appliesTo')}
              <select
                value={draft.accommodationId}
                onChange={(e) => setDraft({ ...draft, accommodationId: e.target.value })}
              >
                <option value="">{t('rules.everyType')}</option>
                {accommodations.map((a) => (
                  <option key={a.id} value={a.id}>{a.label}</option>
                ))}
              </select>
            </label>
            <label>
              {t('rules.from')}
              <input
                type="date" value={draft.from}
                onChange={(e) => setDraft({ ...draft, from: e.target.value })}
              />
            </label>
            <label>
              {t('rules.until')}
              <input
                type="date" value={draft.until}
                onChange={(e) => setDraft({ ...draft, until: e.target.value })}
              />
            </label>
            {/* The pair used to read "Arrivals from / Arrivals before", which
                is half the truth: the same window is tested against the
                DEPARTURE date for a departure rule. An operator writing "do not
                let anybody leave in the middle of Ascension" could not tell
                from the labels whether a stay arriving before it was covered. */}
            <p className="note wide">{t('rules.windowNote')}</p>
          </div>

          <div className="row">
            <label>
              {t('rules.minNights')}
              <input
                type="number" min={1} max={365} value={draft.minNights}
                onChange={(e) => setDraft({ ...draft, minNights: e.target.value })}
              />
            </label>
            <label>
              {t('rules.maxNights')}
              <input
                type="number" min={1} max={365} value={draft.maxNights}
                onChange={(e) => setDraft({ ...draft, maxNights: e.target.value })}
              />
            </label>
            <label>
              {t('rules.effect')}
              <select
                value={draft.effect}
                onChange={(e) => setDraft({ ...draft, effect: e.target.value as Effect })}
              >
                {EFFECTS.map((e) => (
                  <option key={e} value={e}>{t(EFFECT_LABEL[e])}</option>
                ))}
              </select>
            </label>
          </div>
          <p className="note">{t(EFFECT_NOTE[draft.effect])}</p>

          {(['arriveOn', 'departOn'] as const).map((field) => (
            <div className="row" key={field}>
              <span className="k">
                {t(field === 'arriveOn' ? 'rules.arriveOn' : 'rules.departOn')}
              </span>
              {WEEK_ORDER.map((day) => (
                <label key={day} className="inline">
                  <input
                    type="checkbox" checked={draft[field].includes(day)}
                    onChange={() => toggleDay(field, day)}
                  />
                  {t(WEEKDAY_KEYS[day])}
                </label>
              ))}
            </div>
          ))}
          <p className="note">{t('rules.daysNote')}</p>

          <div className="row">
            <label className="wide">
              {t('rules.label')}
              <input
                type="text" value={draft.label} maxLength={120}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              />
            </label>
          </div>
          <p className="note">{t('rules.labelNote')}</p>
          <div className="row">
            <label className="wide">
              {t('rules.note')}
              <input
                type="text" value={draft.note} maxLength={300}
                onChange={(e) => setDraft({ ...draft, note: e.target.value })}
              />
            </label>
          </div>

          <div className="row">
            <button type="button" className="btn primary" disabled={busy} onClick={addGeneral}>
              {draft.id ? t('rules.saveEdit') : t('rules.addGeneral')}
            </button>
            {draft.id && (
              <button type="button" className="btn" onClick={() => setDraft(blank(year))}>
                {t('rules.cancelEdit')}
              </button>
            )}
          </div>
        </fieldset>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* What is in force                                                   */}
      {/* ------------------------------------------------------------------ */}
      <h3>{t('rules.listHeading', { count: sorted.length })}</h3>
      {sorted.length === 0 ? (
        <p className="note">{t('rules.none')}</p>
      ) : (
        <table className="grid">
          <thead>
            <tr>
              <th>{t('rules.colWhat')}</th>
              <th>{t('rules.colWindow')}</th>
              <th>{t('rules.colGuestSees')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.id}>
                <td>
                  {describe(t, r, labelOf(r.accommodationId))}
                  {r.note && <div className="n">{r.note}</div>}
                </td>
                <td>
                  {shortDate(toDay(r.from)!)} – {shortDate(toDay(r.until)!)}
                </td>
                {/* A blocking rule shows the guest nothing but the refusal, so
                    there is no label to report — saying "—" is the truth. */}
                <td>{r.effect === 'block' ? '—' : (r.label || t('rules.noLabel'))}</td>
                <td>
                  <button type="button" className="btn" onClick={() => edit(r)}>
                    {t('rules.edit')}
                  </button>
                  <button type="button" className="btn" onClick={() => remove(r.id)}>
                    {t('rules.remove')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="note">{t('rules.orderNote')}</p>
    </section>
  );
}

/** Exported for the spec, which asserts the sentence rather than the controls. */
export const describeRule = describe;
export { toIso };
